import { StatusBadge } from '@/components/shared/StatusBadge';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate, formatRelativeDate } from '@/lib/formatters';
import type { Principal } from '@/types/principal';

interface PrincipalDetailDialogProps {
  principal: Principal | null;
  onClose: () => void;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-all text-foreground">{value}</dd>
    </div>
  );
}

/**
 * Attribute values are usually strings, but SCIM stores structured objects
 * too (e.g. the name object under attributes.name). Render those as JSON
 * instead of "[object Object]".
 */
function formatAttributeValue(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/**
 * Read-only detail view for a provisioned principal. Surfaces the SCIM
 * external ID and raw provider subject so admins can correlate a Gateco
 * principal with the source record in their IdP.
 */
export function PrincipalDetailDialog({ principal, onClose }: PrincipalDetailDialogProps) {
  const attributes = Object.entries(principal?.attributes ?? {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );

  return (
    <Dialog open={principal !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        {principal && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span className="truncate">{principal.display_name}</span>
                <StatusBadge status={principal.status} />
              </DialogTitle>
              <DialogDescription>{principal.email || 'No email on record'}</DialogDescription>
            </DialogHeader>

            <dl className="divide-y divide-border">
              <DetailRow label="Source" value={principal.identity_provider_name || '—'} />
              <DetailRow
                label="SCIM external ID"
                value={<code className="text-xs">{principal.external_id}</code>}
              />
              <DetailRow
                label="Provider subject"
                value={
                  principal.provider_subject ? (
                    <code className="text-xs">{principal.provider_subject}</code>
                  ) : (
                    '—'
                  )
                }
              />
              <DetailRow
                label="Groups"
                value={
                  (principal.groups ?? []).length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {principal.groups.map((g) => (
                        <Badge key={g} variant="secondary" className="font-normal">
                          {g}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    '—'
                  )
                }
              />
              <DetailRow
                label="Roles"
                value={(principal.roles ?? []).length > 0 ? principal.roles.join(', ') : '—'}
              />
              {attributes.length > 0 && (
                <DetailRow
                  label="Attributes"
                  value={
                    <div className="space-y-0.5">
                      {attributes.map(([k, v]) => (
                        <div key={k} className="text-xs">
                          <span className="text-muted-foreground">{k}:</span>{' '}
                          {formatAttributeValue(v)}
                        </div>
                      ))}
                    </div>
                  }
                />
              )}
              <DetailRow
                label="Last seen"
                value={principal.last_seen ? formatRelativeDate(principal.last_seen) : '—'}
              />
              <DetailRow label="Created" value={formatDate(principal.created_at)} />
            </dl>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
